import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { DEFAULT_OPPORTUNITY_IMAGE_ID, WEME_APP_BASE_URL, WEME_SUPPORT_WHATSAPP_URL, normalizeRegistrationButtonTextForStorage } from "@shared/helpers/constants";
import {
  buildOpportunityPath,
} from "@shared/notifications/notification-navigation";
import { Roles } from "@shared/Roles";
import dayjs from "dayjs";
import {
  NotificationTemplateKey,
} from "@config/notification-templates.config";
import { sendTemplatedUserNotification } from "@shared/notifications/templated-user-notification";
import { clampMatchWeight } from "@shared/helpers/match-weight";
import { syncOpportunityFullFlagFromSeats } from "@shared/helpers/sync-opportunity-full-flag";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import { Allow, remult, repo } from "remult";
import { type Opportunity } from "./entity";

export const OpportunityEntityConfig: HyperEntityConfig<Opportunity> = {
  allowApiRead: Allow.authenticated,
  allowApiCrud: [Roles.Admin, Roles.SuperAdmin, Roles.Manager, Roles.User],
  apiPrefilterPerRole: (filter) => {
    if (!remult.user) return;

    filter(Roles.SuperDuperAdmin, false);

    filter([Roles.Admin, Roles.SuperAdmin], {
      $or: [
        {
          organization: {
            $id: remult.user.organizationId,
          },
        },
        {
          isPublic: true,
        },
      ],
    });

    filter(Roles.Manager, {
      $or: [
        {
          isPublic: true,
        },
        {
          manager: {
            $id: remult.user.id,
          },
        },
        {
          form: {
            $is: {
              team: {
                $is: {
                  id: { $in: remult.user.managingTeams },
                },
              },
            },
          },
        },
        {
          teams: {
            $some: {
              teamId: {
                $in: remult.user.managingTeams,
              },
            },
          },
        },
      ],
    });

    filter(Roles.User, {
      isDeleted: false,
      $or: [
        {
          isPublic: true,
        },
        {
          organization: {
            $id: {
              $in: remult.user.organizations,
            },
          },
        },
        {
          partnerOrganization: {
            $is: {
              managers: {
                $some: {
                  id: remult.user?.id ?? "",
                },
              },
            },
          },
        },
        {
          partnerOrganization: {
            $is: {
              parent: {
                $is: {
                  managers: {
                    $some: {
                      id: remult.user?.id ?? "",
                    },
                  },
                },
              },
            },
          },
        },
        {
          partnerOrganization: {
            $is: {
              parent: {
                $is: {
                  parent: {
                    $is: {
                      managers: {
                        $some: {
                          id: remult.user?.id ?? "",
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      ],
    });
  },

  allowInsertPerRole(allow) {
    allow(
      Roles.OrganizationManagers,
      (entity) => entity.organization!.id === remult.user?.organizationId,
    );
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow([Roles.Admin, Roles.SuperAdmin], {});

    allow(Roles.Manager, {
      $or: [
        {
          manager: {
            $id: remult.user?.id,
          },
        },
        {
          form: {
            $is: {
              team: {
                $is: {
                  id: { $in: remult.user?.managingTeams },
                },
              },
            },
          },
        },
        {
          teams: {
            $some: {
              teamId: {
                $in: remult.user?.managingTeams,
              },
            },
          },
        },
      ],
    });

    allow(Roles.User, {
      associationRequested: {
        $id: {
          $in: remult.user!.associations,
        },
      },
    });
  },

  async saving(entity, e) {
    // Convert empty string foreign key fields to undefined to avoid foreign key constraint violations
    // This allows the database to use its default value instead of trying to validate an empty string
    // This hook runs automatically on every save/insert operation, including bulk inserts
    const emptyStringFields: (keyof Opportunity)[] = [
      "aidRequestFormId",
      "associationRequestedId",
      "managerId",
      "handlingManagerId",
      "repeatedOpportunityId",
      "requestImageId",
    ] as const;

    for (const field of emptyStringFields) {
      const value = (entity as any)[field];
      if (value !== "") continue;
      // Empty associationRequestedId must become null so an update actually
      // clears the FK. Other empty FKs stay omitted (undefined) on save.
      (entity as any)[field] =
        field === "associationRequestedId" ? null : undefined;
    }

    // Empty string is not a valid Gender enum. Use null so an update can
    // clear required_gender (undefined is omitted and the old value stays).
    const requiredGender = (entity as any).requiredGender;
    if (requiredGender === "") {
      (entity as any).requiredGender = null;
    }

    entity.registrationButtonText = normalizeRegistrationButtonTextForStorage(
      entity.registrationButtonText,
    );

    if (entity.isClosed || entity.isDeleted) {
      entity.imageId = DEFAULT_OPPORTUNITY_IMAGE_ID;
      (entity as { requestImageId?: string | null }).requestImageId = null;
    }

    // 1. Handle explicit legacy flags if provided by client (to transition safely)
    if (e.fields.hasTime.valueChanged() && !entity.hasTime) {
      entity.startDate = null;
      entity.endDate = null;
    }
    if (e.fields.hasStartTime.valueChanged() && !entity.hasStartTime) {
      entity.startDate = null;
    }

    // 2. Auto-calculate legacy flags based on current state of dates
    const { startDate, endDate } = entity;
    entity.hasTime = !!(startDate || endDate);
    entity.hasStartTime = !!startDate;
    entity.isGroup = allowsGroupVolunteer(entity.volunteerMode);

    // 3. Auto-calculate lastCallToVolunteers only on insert; on update respect the value from the form
    if (e.isNew) {
      if (!startDate && !endDate) {
        entity.lastCallToVolunteers = dayjs().add(1, "month").toDate();
      } else if (
        startDate &&
        endDate &&
        dayjs(startDate).isSame(dayjs(endDate), "day")
      ) {
        entity.lastCallToVolunteers = startDate;
      } else if (endDate) {
        entity.lastCallToVolunteers = endDate;
      } else if (startDate) {
        entity.lastCallToVolunteers = startDate;
      }
    }
  },

  async onInsert(entity, e) {
    // SMS removed - opportunity request confirmation no longer sent
  },

  async onUpdate(entity, e) {
    // Handle status changes - REQUEST_APPROVED and REQUEST_REJECTED
    if (e.fields.status.valueChanged()) {
      const userRequested = await e.relations.associationRequested.findOne({
        include: {
          user: true,
        },
      });

      const user = userRequested?.user;
      // Instances follow the parent request. One approval message, on the parent.
      if (!entity.parentOpportunityId && (user?.id || user?.phoneNumber)) {
        try {
          if (entity.status === "APPROVED") {
            await sendTemplatedUserNotification({
              templateKey: NotificationTemplateKey.REQUEST_APPROVED,
              placeholders: {
                user_name: user.name || "",
                link: `${WEME_APP_BASE_URL}${buildOpportunityPath(entity.id)}`,
              },
              userId: user.id,
              phoneNumber: user.phoneNumber,
              channels: { push: !!user.id, sms: !!user.phoneNumber, inApp: !!user.id },
              navigateTo: buildOpportunityPath(entity.id),
            });
          } else if (entity.status === "DECLINED") {
            await sendTemplatedUserNotification({
              templateKey: NotificationTemplateKey.REQUEST_REJECTED,
              placeholders: {
                user_name: user.name || "",
                whatsapp_link: WEME_SUPPORT_WHATSAPP_URL,
              },
              phoneNumber: user.phoneNumber,
              channels: { sms: !!user.phoneNumber, push: false, inApp: false },
            });
          }
        } catch (error) {
          console.error(
            "[opportunity/config] Failed to send status change notification:",
            entity.id,
            entity.status,
            error,
          );
        }
      }
    }

    if (
      e.fields.startDate.valueChanged() ||
      e.fields.coordinates.valueChanged()
    ) {
      // SMS removed - opportunity update notification no longer sent to participants
    }

    if (e.fields.amount.valueChanged()) {
      await syncOpportunityFullFlagFromSeats(entity, true);
    }

    if (e.fields.defaultMatchWeight.valueChanged()) {
      const { OpportunityParticipant } = await import(
        "../opportunity-participant/entity"
      );
      await repo(OpportunityParticipant).updateMany({
        where: { opportunityId: entity.id },
        set: { matchWeight: clampMatchWeight(entity.defaultMatchWeight) },
      });
    }
  },
};
